import React, { useState, useMemo } from 'react';
import { Grid2, Stack } from '@mui/material';
import { useAtom } from 'jotai';
import { isMobileAtom, selectedLanguageAtom } from '@atoms/appSettings';
import { tagPageStyles as classes } from './styleObjects/TagPage';
import { PageHelmet } from '@components/PageHelmet';
import { languageRouteTypes } from '@helpers/languageRoutes';
import { cardTypes } from '@helpers/ObjectTypeModels/Card';
import { Card } from '@components/Cards/Card';
import { getLanguageRouteAndPrefixByCodeAndRouteKey } from '@helpers/languages';
import { getLazyLoadByIndex } from '@helpers/lazyloading';
import { Heading, Pagination, PaginationItem, V2Button, containerWidth } from '@web-for-marketing/react-ui';
import { CheckboxInputButton } from '@components/CheckboxInputButton';
import { useStaticValues } from '@stateManagement/StaticValuesContext';
import { getTranslation } from '@helpers/languageTranslations';
import { useTranslation } from '@stateManagement/TranslationContext';
import { LanguageRouteKey } from '@models/languageRoute';
import { LanguageCode } from '@models/languageCode';
import { NullableTagPageData } from '@models/tagPage';
import { SectionContainer } from '@components/SectionComponents/SectionContainer';

const taggedEntityTypeRouteTypeMap: Record<string, LanguageRouteKey> = {
    BlogArticle: languageRouteTypes.BLOG_POST,
    SuccessStoryArticle: languageRouteTypes.CASE_STUDY,
    PressReleaseArticle: languageRouteTypes.PRESS_RELEASE,
    WhitePaperArticle: languageRouteTypes.WHITE_PAPER,
    GlossaryArticle: languageRouteTypes.GLOSSARY,
    Video: languageRouteTypes.VIDEO,
};

interface Filter {
    name: string;
    value: boolean;
    type: string;
}

function constructQuery(filterState: Filter[]): string {
    let filteredCount = 0;

    return filterState.reduce((acc, filter) => {
        if (!filter.value) {
            return acc;
        } else {
            filteredCount++;
            return filteredCount - 1 > 0 ? `${acc}&filters=${filter.type}` : `${acc}filters=${filter.type}`;
        }
    }, '');
}

function getUpdatedFilterState(filterState: Filter[], filterStrings: string[] | null | undefined): Filter[] {
    return filterState.map((filter) => {
        if (filterStrings && filterStrings.includes(filter.type)) {
            return { ...filter, value: true };
        } else {
            return { ...filter, value: false };
        }
    });
}

function generateBasePathWithQuery(
    selectedLanguage: LanguageCode,
    tagPageData: NullableTagPageData,
    filterState: Filter[]
): string {
    if (getActiveFilterValueCount(filterState) > 0) {
        return `${getLanguageRouteAndPrefixByCodeAndRouteKey(selectedLanguage, languageRouteTypes.TAG)}${
            tagPageData?.tag.slug
        }?${constructQuery(filterState)}`;
    } else {
        return `${getLanguageRouteAndPrefixByCodeAndRouteKey(selectedLanguage, languageRouteTypes.TAG)}${
            tagPageData?.tag.slug
        }`;
    }
}

function getActiveFilterValueCount(filterState: Filter[]): number {
    const filters = filterState.filter((filter) => filter.value === true);
    return filters.length;
}

export default function TagPage(): JSX.Element {
    const { selectedLanguageTranslation } = useTranslation();
    const articleTypesTranslate = getTranslation(selectedLanguageTranslation, 'articleTypes');
    const noBlogsFoundForTagTranslation = useMemo(
        () => getTranslation(selectedLanguageTranslation, 'noBlogsFound'),
        [selectedLanguageTranslation]
    );

    const filters: Filter[] = [
        { name: articleTypesTranslate.blog, value: false, type: 'blog' },
        { name: articleTypesTranslate.caseStudy, value: false, type: 'case-study' },
        { name: articleTypesTranslate.pressRelease, value: false, type: 'press-release' },
        { name: articleTypesTranslate.whitePaper, value: false, type: 'white-paper' },
        { name: articleTypesTranslate.glossary, value: false, type: 'glossary' },
        { name: articleTypesTranslate.video, value: false, type: 'video' },
    ];

    const [isMobile] = useAtom(isMobileAtom);
    const [selectedLanguage] = useAtom(selectedLanguageAtom);
    const { tagPageData } = useStaticValues();
    const [filterState, setFilterState] = useState<Filter[]>(
        () => getUpdatedFilterState(filters, tagPageData?.filters) || filters
    );
    const [filtersOpen, setFiltersOpen] = useState(!!tagPageData?.filters?.length);
    const notLazyLoadedAmount = isMobile ? 2 : 8;
    const filterValueCount = useMemo(() => getActiveFilterValueCount(filterState), [filterState]);
    const filterValueSet = filterValueCount > 0;
    const basePathWithQuery = useMemo(
        () => generateBasePathWithQuery(selectedLanguage, tagPageData, filterState),
        [selectedLanguage, tagPageData, filterState]
    );
    const paginationUrlPrefix = filterValueSet ? `${basePathWithQuery.split('?')[0]}` : basePathWithQuery;
    const paginationUrlQueryString = filterValueSet ? `?${basePathWithQuery.split('?')[1]}` : '';

    const filterTranslate = getTranslation(selectedLanguageTranslation, 'articleTypesAction');

    const handleChange = (type: Filter, index: number) => (value: boolean) => {
        const newFilterState = [...filterState];
        newFilterState[index] = { ...type, value };
        setFilterState([...newFilterState]);
        const generatedPath = generateBasePathWithQuery(selectedLanguage, tagPageData, newFilterState);
        window.location.href = `${generatedPath}`;
    };

    return (
        <div css={classes.headerBackground}>
            {tagPageData?.pagedResult ? (
                <SectionContainer maxWidth={containerWidth.lg}>
                    <PageHelmet
                        seo={{
                            title: `${tagPageData.tag.title || tagPageData.tag.name}`,
                            description: tagPageData.tag.description,
                            dateModified: tagPageData.tag.modifiedDateUtc,
                            datePublished: tagPageData.tag.publishedDateUtc,
                        }}
                        noIndex={tagPageData.tag.noIndex}
                    />
                    <Heading variant='h2' component='h1' color='primary' css={classes.mainTitle}>
                        {tagPageData.tag.name}
                    </Heading>
                    <div
                        css={[
                            classes.filterPageContainer,
                            {
                                justifyContent:
                                    tagPageData.pagedResult.totalRecordCount > tagPageData.pagedResult.maxRecordsPerPage
                                        ? 'space-between'
                                        : 'flex-end',
                            },
                        ]}
                    >
                        <V2Button
                            variant='secondary'
                            onClick={() => setFiltersOpen(!filtersOpen)}
                            aria-label='Show filters'
                            size='large'
                        >
                            {filterValueSet ? `Filters (${filterValueCount})` : filterTranslate.filter || 'Filter'}
                        </V2Button>
                    </div>
                    {filtersOpen ? (
                        <Stack direction='row' css={classes.filterContainer}>
                            {filterState.map((filter, index) => (
                                <div key={filter.name} css={classes.labelContainer}>
                                    <CheckboxInputButton
                                        label={filter.name}
                                        name={filter.name}
                                        checked={filter.value}
                                        onChange={handleChange(filter, index)}
                                    />
                                </div>
                            ))}
                        </Stack>
                    ) : null}
                    <Grid2 container css={classes.cardContainer} columnSpacing='4%' rowSpacing='4rem'>
                        {tagPageData.pagedResult.pageRecords.length ? (
                            tagPageData.pagedResult.pageRecords.map((taggedEntityPreview, index) => {
                                const cardContent = {
                                    title: taggedEntityPreview.title,
                                    image: {
                                        imagePath: taggedEntityPreview.cardImageUrl,
                                        mobilePath: taggedEntityPreview.mobileCardImageUrl,
                                        imageAlt: taggedEntityPreview.cardImageAlt,
                                        lazyLoad: getLazyLoadByIndex(index, notLazyLoadedAmount),
                                    },
                                    publishedDateUtc: taggedEntityPreview.publishedDateUtc,
                                };

                                return (
                                    <Grid2 size={{ xs: 12, md: 6, lg: 3 }} key={taggedEntityPreview.slug}>
                                        <Card
                                            card={{
                                                ...cardTypes.BLOG_FOUR_COLUMN,
                                                content: cardContent,
                                            }}
                                            href={`${getLanguageRouteAndPrefixByCodeAndRouteKey(
                                                selectedLanguage,
                                                taggedEntityTypeRouteTypeMap[taggedEntityPreview.type]
                                            )}${taggedEntityPreview.slug}`}
                                            data-testid={`tagCard${index}`}
                                        />
                                    </Grid2>
                                );
                            })
                        ) : (
                            <Heading component='h2'>{noBlogsFoundForTagTranslation.noArticlesFound}</Heading>
                        )}
                    </Grid2>
                    {tagPageData.pagedResult.totalRecordCount > tagPageData.pagedResult.maxRecordsPerPage ? (
                        <Stack alignItems='center' css={classes.pagination}>
                            <Pagination
                                currentPage={tagPageData.pagedResult.currentPage}
                                totalRecordCount={tagPageData.pagedResult.totalRecordCount}
                                maxRecordsPerPage={tagPageData.pagedResult.maxRecordsPerPage}
                                renderItem={(item) => (
                                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                                    <PaginationItem
                                        component='a'
                                        href={`${paginationUrlPrefix}${item.page}/${paginationUrlQueryString}`}
                                        {...item}
                                    />
                                )}
                            />
                        </Stack>
                    ) : null}
                </SectionContainer>
            ) : (
                <Heading variant='h2' component='h1'>
                    {`${noBlogsFoundForTagTranslation.forTag} ${tagPageData ? tagPageData.tag.name : ''}`}
                </Heading>
            )}
        </div>
    );
}
